/**
 * Aspect-control registry — the UI half of a light language pack (§language
 * packs). A control gives an aspect key a first-class inspector editor instead
 * of the raw string field, keyed to the same aspect-key CONVENTION the pack's
 * engine-side procedures + docs use. No parser change: the control just reads
 * and writes the freeform `aspect key: value` the inspector already edits.
 *
 * The engine-side half (procedures + docs) lives in `@archlang/engine`'s pack
 * registry; controls are React, so they register here (the app-shell realm).
 * A product wires both halves for the packs it opts into.
 */
import type { ComponentType } from "react";

/** Props an aspect-control editor receives at the inspector row: the concrete
 *  aspect `path` (e.g. `"slo.p95"`), its current string `value`, and a commit
 *  callback that writes the edited value back through the normal aspect set
 *  path (an empty commit is ignored by the caller, matching `InlineText`). */
export interface AspectControlProps {
  readonly path: string;
  readonly value: string;
  onCommit(value: string): void;
}

/** A first-class inspector editor for an aspect-key convention. `aspectKey` is
 *  the governed prefix (e.g. `"slo"`); it matches the key itself and any dotted
 *  sub-key (`slo.p95`, `slo.availability`). */
export interface AspectControl {
  readonly aspectKey: string;
  readonly Editor: ComponentType<AspectControlProps>;
}

const controls: AspectControl[] = [];

/** Register an aspect control. First registration for a key prefix wins on a
 *  match (see {@link resolveAspectControl}). */
export function registerAspectControl(control: AspectControl): void {
  controls.push(control);
}

/** The control governing an aspect `path`, if any — the key itself or a dotted
 *  sub-key of a registered prefix. */
export function resolveAspectControl(path: string): AspectControl | undefined {
  return controls.find((c) => path === c.aspectKey || path.startsWith(`${c.aspectKey}.`));
}

/** Drop all registered controls — test hygiene between cases. */
export function clearAspectControls(): void {
  controls.length = 0;
}
